import { useEffect, useMemo, useState } from 'react';
import { Lock, ShieldCheck, KeyRound, RefreshCw, Unlock, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import useFinanceLockStatus from '../hooks/useFinanceLockStatus';
import {
  changeFinancePin,
  disableFinanceLock,
  enableFinanceLock,
  lockFinance,
  unlockFinance,
} from '../services/financeLockApi';

const FINANCE_LOCK_EXPLANATION =
  'Finance Lock protects revenue, reports, EMI, setup costs, purchase values, and other money data.';

function normalizePin(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 4);
}

function validatePinPair(pin, confirmPin) {
  if (!/^\d{4}$/.test(pin)) {
    return 'Finance PIN must be exactly 4 digits.';
  }

  if (confirmPin !== undefined && pin !== confirmPin) {
    return 'PIN and confirmation PIN must match.';
  }

  return '';
}

function getFriendlyFinanceError(error) {
  const backendMessage = error?.response?.data?.error || error?.message || '';
  const backendCode = error?.response?.data?.code;

  if (backendCode === 'FINANCE_PIN_TEMP_LOCKED' || error?.response?.status === 423) {
    return 'Too many failed attempts. Try again later.';
  }

  return backendMessage || 'Finance Lock request failed.';
}

export function FinanceLockSettingsPanel({ status, onChanged, compact = false }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState('');
  const financeLock = status.financeLock || {};

  if (!status.canManage) {
    return null;
  }

  const resetPins = () => {
    setPin('');
    setConfirmPin('');
  };

  const savePin = async () => {
    const validationError = validatePinPair(pin, confirmPin);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving('pin');
    try {
      await changeFinancePin(pin);
      resetPins();
      toast.success(financeLock.hasPin ? 'Finance PIN changed' : 'Finance PIN created');
      await onChanged();
    } catch (error) {
      toast.error(getFriendlyFinanceError(error));
    } finally {
      setSaving('');
    }
  };

  const enableLock = async () => {
    const needsPin = !financeLock.hasPin;
    if (needsPin) {
      const validationError = validatePinPair(pin, confirmPin);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setSaving('enable');
    try {
      await enableFinanceLock(needsPin ? pin : undefined);
      resetPins();
      toast.success('Finance Lock enabled');
      await onChanged();
    } catch (error) {
      toast.error(getFriendlyFinanceError(error));
    } finally {
      setSaving('');
    }
  };

  const disableLock = async () => {
    setSaving('disable');
    try {
      await disableFinanceLock();
      toast.success('Finance Lock disabled');
      await onChanged();
    } catch (error) {
      toast.error(getFriendlyFinanceError(error));
    } finally {
      setSaving('');
    }
  };

  return (
    <section className={`${compact ? 'rounded-[24px] p-4' : 'rounded-[24px] p-5'} border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]`}>
      <div className={`${compact ? 'flex flex-col gap-4' : 'flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'}`}>
        <div className="flex gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">
              Finance Lock
            </p>
            <h2 className="mt-1.5 text-lg font-semibold text-slate-900">
              {financeLock.enabled ? 'Enabled' : 'Disabled'}
            </h2>
            <p className="mt-1.5 max-w-3xl text-sm leading-6 text-slate-500">
              {FINANCE_LOCK_EXPLANATION}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {financeLock.enabled ? (
            <button
              type="button"
              onClick={disableLock}
              disabled={Boolean(saving)}
              className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-60"
            >
              <LogOut className="h-4 w-4" />
              {saving === 'disable' ? 'Disabling...' : 'Disable'}
            </button>
          ) : (
            <button
              type="button"
              onClick={enableLock}
              disabled={Boolean(saving)}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              <Lock className="h-4 w-4" />
              {saving === 'enable' ? 'Enabling...' : 'Enable'}
            </button>
          )}
        </div>
      </div>

      <div className={`${compact ? 'mt-5 grid grid-cols-1 gap-3' : 'mt-5 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,180px)_minmax(0,180px)_auto] md:items-end'}`}>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {financeLock.hasPin ? 'New PIN' : 'Create PIN'}
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={4}
            value={pin}
            onChange={(event) => setPin(normalizePin(event.target.value))}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-lg font-semibold tracking-[0.35em] text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
            placeholder="0000"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Confirm PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={4}
            value={confirmPin}
            onChange={(event) => setConfirmPin(normalizePin(event.target.value))}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-lg font-semibold tracking-[0.35em] text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
            placeholder="0000"
          />
        </div>

        <button
          type="button"
          onClick={savePin}
          disabled={Boolean(saving)}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
        >
          <KeyRound className="h-4 w-4" />
          {saving === 'pin' ? 'Saving PIN...' : financeLock.hasPin ? 'Change PIN' : 'Create PIN'}
        </button>
      </div>
    </section>
  );
}

function LockedFinancePanel({ title, description, status, onUnlocked, onCancel, compact }) {
  const [pin, setPin] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState('');

  const submitUnlock = async (event) => {
    event?.preventDefault?.();
    const validationError = validatePinPair(pin);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUnlocking(true);
    setError('');
    try {
      await unlockFinance(pin);
      setPin('');
      toast.success('Finance data unlocked');
      await onUnlocked();
    } catch (unlockError) {
      setError(getFriendlyFinanceError(unlockError));
    } finally {
      setUnlocking(false);
    }
  };

  if (status.restricted || !status.canUnlock) {
    return (
      <div className={`${compact ? 'rounded-[20px] p-4' : 'rounded-[24px] p-6'} border border-amber-200 bg-amber-50 text-amber-800`}>
        <div className="flex gap-3">
          <Lock className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Finance access restricted</p>
            <p className="mt-1 text-sm leading-6">
              Staff users cannot unlock finance data. Ask the clinic owner/admin or accountant to open this section.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${compact ? 'rounded-[20px] p-4' : 'rounded-[24px] p-6'} border border-blue-100 bg-blue-50`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-blue-600">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">
              Finance Lock
            </p>
            <h2 className="mt-1.5 text-lg font-semibold text-slate-900">
              {title || 'Finance Lock enabled. Enter PIN to continue.'}
            </h2>
            <p className="mt-1.5 text-sm leading-6 text-slate-600">
              {description || 'Finance Lock enabled. Enter PIN to continue.'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={submitUnlock} className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 sm:w-44">
          <input
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            maxLength={4}
            value={pin}
            onChange={(event) => setPin(normalizePin(event.target.value))}
            className="w-full rounded-2xl border border-blue-200 bg-white px-4 py-3 text-center text-lg font-semibold tracking-[0.35em] text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
            placeholder="0000"
            aria-label="Finance PIN"
          />
          {error ? <p className="mt-2 text-sm font-semibold text-rose-600">{error}</p> : null}
        </div>

        <button
          type="submit"
          disabled={unlocking}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
        >
          <Unlock className="h-4 w-4" />
          {unlocking ? 'Unlocking...' : 'Unlock'}
        </button>

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
        ) : null}
      </form>
    </div>
  );
}

export function FinanceProtectedValue({ children, label = 'Locked' }) {
  const status = useFinanceLockStatus();

  if (status.loading) {
    return <span className="inline-block h-5 w-20 animate-pulse rounded-full bg-slate-200" />;
  }

  if (status.restricted || !status.canUnlock) {
    return <span className="text-sm font-semibold text-amber-600">Restricted</span>;
  }

  if (status.locked) {
    return <span className="text-sm font-semibold text-blue-600">{label}</span>;
  }

  return children;
}

export default function FinanceLockGate({
  children,
  compact = false,
  description,
  onAccessGranted,
  onCancel,
  renderLockedLayout,
  showSettings = false,
  title = 'Finance Lock enabled. Enter PIN to continue.',
}) {
  const status = useFinanceLockStatus();
  const [accessNotified, setAccessNotified] = useState(false);

  const canAccess = !status.loading && !status.error && status.unlocked && !status.restricted;

  useEffect(() => {
    if (canAccess && !accessNotified) {
      setAccessNotified(true);
      onAccessGranted?.();
    }
  }, [accessNotified, canAccess, onAccessGranted]);

  useEffect(() => {
    if (!canAccess) {
      setAccessNotified(false);
    }
  }, [canAccess]);

  const content = useMemo(() => {
    if (status.loading) {
      return (
        <div className={`${compact ? 'rounded-[20px] p-4' : 'section-card p-6'} text-sm text-slate-500`}>
          Checking Finance Lock...
        </div>
      );
    }

    if (status.error && !status.restricted) {
      return (
        <div className={`${compact ? 'rounded-[20px] p-4' : 'section-card p-6'} border border-rose-200 bg-rose-50 text-rose-700`}>
          <div className="flex items-start gap-3">
            <RefreshCw className="mt-0.5 h-5 w-5" />
            <div>
              <p className="font-semibold">Could not check Finance Lock</p>
              <p className="mt-1 text-sm">{status.error}</p>
              <button
                type="button"
                onClick={status.refresh}
                className="mt-3 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!canAccess) {
      const lockedPanel = (
        <LockedFinancePanel
          compact={compact}
          description={description}
          onCancel={onCancel}
          onUnlocked={status.refresh}
          status={status}
          title={title}
        />
      );

      return renderLockedLayout ? renderLockedLayout(lockedPanel, status) : lockedPanel;
    }

    return typeof children === 'function' ? children(status) : children;
  }, [canAccess, children, compact, description, onCancel, renderLockedLayout, status, title]);

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {showSettings ? (
        <FinanceLockSettingsPanel compact={compact} status={status} onChanged={status.refresh} />
      ) : null}
      {content}
    </div>
  );
}
